import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg, { buildBreadcrumbSchema, buildArticleSchema, buildFaqSchema } from '@/components/feature/SchemaOrg';
import { articleData, articleFaqs } from '@/mocks/article';
import ArticleSidebar from './components/ArticleSidebar';

function formatViews(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(n);
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  if (!id || !articleData[id]) {
    return <Navigate to="/" replace />;
  }

  const article = articleData[id];

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.category, to: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 凪光在线观看`,
          url: `https://subtitlesjp.github.io/article/${article.id}`,
          breadcrumb: { '@type': 'BreadcrumbList', ...buildBreadcrumbSchema(breadcrumbItems.map(b => ({ name: b.label, url: b.to }))) },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={buildBreadcrumbSchema(breadcrumbItems.map(b => ({ name: b.label, url: b.to })))}
      />
      <SchemaOrg
        type="Article"
        data={buildArticleSchema({
          title: article.title,
          description: article.subtitle,
          image: article.cover,
          datePublished: article.updateDate,
          dateModified: article.updateDate,
          url: `/article/${article.id}`,
        })}
      />
      <SchemaOrg
        type="FAQPage"
        data={buildFaqSchema(articleFaqs.map(f => ({ question: f.question, answer: f.answer })))}
      />

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <article className="bg-background-50">
        <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 凪光在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8">
            <div className="mx-auto max-w-[1400px]">
              <div className="flex items-center gap-3 mb-3">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="whitespace-nowrap px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium cursor-pointer"
                >
                  {article.category}
                </Link>
                {article.rating > 0 && (
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong>{article.rating}</strong>
                    <span className="text-foreground-400">分</span>
                  </span>
                )}
                <span className="text-sm text-foreground-400">
                  {formatViews(article.views)}次播放
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
                {article.title}
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
                {article.subtitle}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 md:px-6 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-4 mb-8 p-4 bg-background-100 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-play-list-2-line text-foreground-400"></i>
                  <span>共 <strong>{article.episodes}</strong> 集</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-calendar-line text-foreground-400"></i>
                  <span>更新于 {article.updateDate}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-medium ${
                    article.status.includes('完结') || article.status.includes('上映') || article.status.includes('发售') || article.status.includes('发布')
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {article.status}
                  </span>
                </div>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">制作团队</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {article.staff.map((s) => (
                    <div key={s.name} className="p-3 bg-background-100 rounded-lg">
                      <p className="text-sm font-medium text-foreground-900">{s.name}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{s.role}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <ArticleSidebar article={article} />
          </div>

          <div className="mt-12 max-w-3xl">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">常见问题</h2>
            <div className="space-y-3">
              {articleFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center transition-transform duration-300 ${
                      faqOpen === index ? 'rotate-45' : ''
                    }`}>
                      <i className="ri-add-line text-foreground-500 text-lg"></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      faqOpen === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}